import type { OwnershipModel, Relation } from '../model';
import { ForestHelper, type Forest } from './forest-helper';
import { LayoutGeometryHelper as G } from './layout-geometry-helper';
import { LeafColumnHelper } from './leaf-column-helper';

export interface Lane {
  relation: Relation;
  blockId: number;
}

export interface SharedPlacement {
  entityId: number;
  hostId: number;
  rowIndex: number;
  lanes: Lane[];
}

export interface MultiOwnerResolution {
  forest: Forest;
  shared: SharedPlacement[];
  chipped: Relation[];
}

interface OwnerSlot {
  hostId: number;
  blockId: number;
  rowIndex: number;
  column: number;
  isLastRow: boolean;
}

class MultiOwnerHelper {
  // Lanes are drawn only when every owner is the top of a block, or a subsidiary facing open space on its
  // right, in neighbouring blocks of one wrap row. Every other extra owner becomes a pair of chips.
  static resolve(
    model: Readonly<OwnershipModel>,
    loopIds: ReadonlySet<string>,
    chippedLoops: ReadonlyArray<Relation>,
    excluded: ReadonlySet<number> = new Set()
  ): MultiOwnerResolution {
    const base = ForestHelper.build(model, loopIds);
    const candidates = [
      ...new Set(base.secondaryRelations.map((relation) => relation.ownedId)),
    ]
      .filter((id) => !excluded.has(id))
      .sort((a, b) => a - b);
    const accepted = candidates.reduce<SharedPlacement[]>((kept, entityId) => {
      const trial = MultiOwnerHelper.tryPlace(model, loopIds, [
        ...kept.map((placement) => placement.entityId),
        entityId,
      ]);
      return trial ?? kept;
    }, []);
    const forest = ForestHelper.build(
      model,
      loopIds,
      new Set(accepted.map((placement) => placement.entityId))
    );
    const sharedIds = new Set(accepted.map((placement) => placement.entityId));
    const chipped = [
      ...forest.secondaryRelations.filter(
        (relation) => !sharedIds.has(relation.ownedId)
      ),
      ...chippedLoops,
    ];
    // A chip beside an owner's node would sit on its lane, so that subsidiary falls back to chips too.
    const withChips = new Set(
      chipped.flatMap((relation) => [relation.ownerId, relation.ownedId])
    );
    const blocked = accepted.find((placement) =>
      placement.lanes.some(
        (lane) =>
          lane.blockId === lane.relation.ownerId && withChips.has(lane.blockId)
      )
    );
    if (blocked)
      return MultiOwnerHelper.resolve(
        model,
        loopIds,
        chippedLoops,
        new Set([...excluded, blocked.entityId])
      );
    return { forest, shared: accepted, chipped };
  }

  static getHostKey(hostId: number, rowIndex: number): string {
    return `${hostId}:${rowIndex}`;
  }

  private static tryPlace(
    model: Readonly<OwnershipModel>,
    loopIds: ReadonlySet<string>,
    entityIds: ReadonlyArray<number>
  ): SharedPlacement[] | null {
    const forest = ForestHelper.build(model, loopIds, new Set(entityIds));
    const placements = entityIds.map((entityId) =>
      MultiOwnerHelper.place(forest, entityId)
    );
    if (placements.some((placement) => placement === null)) return null;
    const valid = placements.filter(
      (placement): placement is SharedPlacement => placement !== null
    );
    const keys = new Set(
      valid.map((placement) =>
        MultiOwnerHelper.getHostKey(placement.hostId, placement.rowIndex)
      )
    );
    return keys.size === valid.length ? valid : null;
  }

  private static place(
    forest: Readonly<Forest>,
    entityId: number
  ): SharedPlacement | null {
    const relations = forest.incomingOf.get(entityId) ?? [];
    const slots = relations.map((relation) =>
      MultiOwnerHelper.getSlot(forest, relation.ownerId)
    );
    const [first] = slots;
    if (relations.length < 2 || !first || slots.some((slot) => slot === null))
      return null;
    const placed = slots.filter((slot): slot is OwnerSlot => slot !== null);
    if (
      placed.some(
        (slot) =>
          slot.hostId !== first.hostId || slot.rowIndex !== first.rowIndex
      )
    )
      return null;
    if (first.hostId !== ForestHelper.VIRTUAL_ROOT && !first.isLastRow)
      return null;
    const columns = [...new Set(placed.map((slot) => slot.column))];
    if (Math.max(...columns) - Math.min(...columns) + 1 !== columns.length)
      return null;
    return {
      entityId,
      hostId: first.hostId,
      rowIndex: first.rowIndex,
      lanes: relations.map((relation, i) => ({
        relation,
        blockId: placed[i].blockId,
      })),
    };
  }

  private static getSlot(
    forest: Readonly<Forest>,
    ownerId: number
  ): OwnerSlot | null {
    const parentId = forest.primaryRelationOf.get(ownerId)?.ownerId;
    let blockId = ownerId;
    if (ForestHelper.isLeaf(forest, ownerId) && parentId !== undefined) {
      const leaves = ForestHelper.getChildren(forest, parentId).filter((id) =>
        ForestHelper.isLeaf(forest, id)
      );
      const index = leaves.indexOf(ownerId);
      if (
        index < 0 ||
        !LeafColumnHelper.isInOutermostRightColumn(leaves.length, index)
      )
        return null;
      blockId = parentId;
    }
    const hostId =
      forest.primaryRelationOf.get(blockId)?.ownerId ??
      ForestHelper.VIRTUAL_ROOT;
    const siblings =
      hostId === ForestHelper.VIRTUAL_ROOT
        ? forest.roots
        : ForestHelper.getChildren(forest, hostId).filter(
            (id) => !ForestHelper.isLeaf(forest, id)
          );
    const index = siblings.indexOf(blockId);
    if (index < 0) return null;
    return {
      hostId,
      blockId,
      rowIndex: Math.floor(index / G.GROUPS_PER_ROW),
      column: index % G.GROUPS_PER_ROW,
      isLastRow:
        Math.floor(index / G.GROUPS_PER_ROW) ===
        Math.ceil(siblings.length / G.GROUPS_PER_ROW) - 1,
    };
  }
}

export { MultiOwnerHelper };
